import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Search, X, Heart, Grid3x3, List, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import TopNav from "@/components/colourmatch/TopNav";
import usePageMeta from "@/hooks/usePageMeta";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const FAVOURITES_KEY = "colourmatch:favourites";
const PAGE_SIZE = 40;

const loadFavourites = () => {
  try { return JSON.parse(localStorage.getItem(FAVOURITES_KEY) || "[]"); }
  catch { return []; }
};
const saveFavourites = (list) => {
  try { localStorage.setItem(FAVOURITES_KEY, JSON.stringify(list)); } catch { /* ignore */ }
};

/**
 * All Colours Library (Phase 1.x, LOCKED 2026-02).
 *
 * Mental Model Fit — this page is another way of completing STEP 3 of the
 * locked workflow (Choose a colour). Selecting a colour with a `?from=workspace`
 * context pre-selects it back on the Workspace but does NOT auto-apply —
 * Apply remains the customer's final action.
 */
export default function AllColours() {
  usePageMeta({ title: "All Colours · ColourMatch" });
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const fromWorkspace = sp.get("from") === "workspace";
  const projectId = sp.get("project");

  const [palette, setPalette] = useState(null);
  const [tab, setTab] = useState("all");            // "recommended" | "all" | "favourites"
  const [q, setQ] = useState("");
  const [familyFilter, setFamilyFilter] = useState(null);
  const [lightness, setLightness] = useState(null);
  const [warmth, setWarmth] = useState(null);
  const [view, setView] = useState("grid");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [favourites, setFavourites] = useState(loadFavourites);

  useEffect(() => {
    axios.get(`${API_BASE}/api/palette`).then((r) => setPalette(r.data)).catch(() => setPalette({ all: [] }));
  }, []);

  const toggleFav = (hex) => {
    const H = hex.toUpperCase();
    setFavourites((prev) => {
      const next = prev.includes(H) ? prev.filter((h) => h !== H) : [...prev, H];
      saveFavourites(next);
      return next;
    });
  };

  const filtered = useMemo(() => {
    if (!palette?.all) return [];
    let pool = palette.all;
    if (tab === "recommended") pool = pool.filter((c) => c.popular || c.designer_pick);
    else if (tab === "favourites") pool = pool.filter((c) => favourites.includes((c.hex || "").toUpperCase()));
    if (familyFilter) pool = pool.filter((c) => c.library_family === familyFilter);
    if (lightness) pool = pool.filter((c) => c.lightness_bucket === lightness);
    if (warmth) pool = pool.filter((c) => c.warmth_bucket === warmth);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      pool = pool.filter((c) =>
        (c.name || "").toLowerCase().includes(s) ||
        (c.code || "").toLowerCase().includes(s) ||
        (c.library_family || "").toLowerCase().includes(s));
    }
    return pool;
  }, [palette, tab, familyFilter, lightness, warmth, q, favourites]);

  // Reset infinite-scroll counter when filters/tab/search change.
  useEffect(() => { setVisible(PAGE_SIZE); }, [tab, familyFilter, lightness, warmth, q]);

  // Infinite-scroll on window scroll near bottom.
  useEffect(() => {
    const onScroll = () => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 400) {
        setVisible((v) => Math.min(v + PAGE_SIZE, filtered.length));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [filtered.length]);

  const onPickColour = (colour) => {
    if (fromWorkspace && projectId) {
      // Locked design: return to Workspace with the colour pre-selected;
      // do not auto-apply — Apply remains the final action.
      const params = new URLSearchParams({
        preset_hex: colour.hex,
        preset_name: colour.name,
      });
      nav(`/visualiser/p/${projectId}?${params.toString()}`);
    } else {
      // Standalone: copy the code and give a hint about applying.
      try { navigator.clipboard.writeText(colour.code || colour.hex); toast.success(`${colour.code} copied`); }
      catch { toast.message(`${colour.code} · ${colour.name}`); }
    }
  };

  const totalInPalette = palette?.all?.length || 0;
  const shownCount = Math.min(visible, filtered.length);
  const familyCounts = palette?.library_filters?.family_counts || {};
  const familyOrder = palette?.library_filters?.families || [];

  return (
    <div className="min-h-screen bg-brand-linen/30">
      <TopNav />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="all-colours-page">
        {/* Contextual back-to-workspace strip */}
        {fromWorkspace && (
          <Link
            to={projectId ? `/visualiser/p/${projectId}` : "/projects"}
            className="inline-flex items-center gap-1.5 text-xs text-brand-muted hover:text-brand-ink mb-3"
            data-testid="all-colours-back-workspace"
          >
            <ArrowLeft size={13} /> Back to your project
          </Link>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="overline text-brand-teal mb-2">ColourMatch Library</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-brand-ink">All Colours</h1>
          <p className="text-sm text-brand-muted mt-3 max-w-2xl leading-relaxed">
            Every colour in the ColourMatch library — {totalInPalette.toLocaleString()} of them. Search, filter, favourite,
            and pick your next hero.
          </p>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, code or family"
              data-testid="colours-search"
              className="w-full h-11 pl-10 pr-9 rounded-2xl border border-brand-border bg-white text-sm placeholder:text-brand-muted focus:outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20 transition"
            />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted hover:text-brand-ink"><X size={14} /></button>
            )}
          </div>

          <div className="inline-flex bg-white border border-brand-border rounded-full p-0.5" data-testid="colours-tabs">
            {[
              { k: "recommended", label: "Recommended" },
              { k: "all",         label: "All Colours" },
              { k: "favourites",  label: "Favourites" },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`colours-tab-${t.k}`}
                className={`h-9 px-4 rounded-full text-xs font-semibold transition-colors ${
                  tab === t.k ? "bg-brand-navy text-white" : "text-brand-ink hover:bg-brand-linen/50"
                }`}
              >
                {t.label}
                {t.k === "favourites" && favourites.length > 0 && (
                  <span className="ml-1.5 text-[10px] opacity-80">({favourites.length})</span>
                )}
              </button>
            ))}
          </div>

          <div className="ml-auto inline-flex items-center gap-2">
            <div className="text-[11px] text-brand-muted whitespace-nowrap" data-testid="colours-counter">
              Showing {shownCount.toLocaleString()} of {filtered.length.toLocaleString()} colours
            </div>
            <div className="inline-flex bg-white border border-brand-border rounded-full p-0.5">
              <button onClick={() => setView("grid")} title="Grid" data-testid="colours-view-grid"
                className={`w-8 h-8 rounded-full inline-flex items-center justify-center ${view === "grid" ? "bg-brand-navy text-white" : "text-brand-muted hover:text-brand-ink"}`}>
                <Grid3x3 size={13} />
              </button>
              <button onClick={() => setView("list")} title="List" data-testid="colours-view-list"
                className={`w-8 h-8 rounded-full inline-flex items-center justify-center ${view === "list" ? "bg-brand-navy text-white" : "text-brand-muted hover:text-brand-ink"}`}>
                <List size={13} />
              </button>
            </div>
          </div>
        </div>

        {/* Body — filter sidebar + grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-6">
          <aside className="bg-white border border-brand-border rounded-3xl p-5 h-fit lg:sticky lg:top-24" data-testid="colours-filters">
            <FilterGroup label="Colour Family">
              {familyOrder.map((f) => (
                <FilterOption
                  key={f}
                  label={f}
                  count={familyCounts[f] || 0}
                  active={familyFilter === f}
                  onClick={() => setFamilyFilter((prev) => prev === f ? null : f)}
                  testid={`filter-family-${f.toLowerCase()}`}
                />
              ))}
            </FilterGroup>

            <FilterGroup label="Lightness">
              {["bright", "mid", "deep"].map((v) => (
                <FilterOption key={v} label={v} active={lightness === v}
                  onClick={() => setLightness((p) => p === v ? null : v)}
                  testid={`filter-lightness-${v}`} />
              ))}
            </FilterGroup>

            <FilterGroup label="Warmth">
              {["warm", "neutral", "cool"].map((v) => (
                <FilterOption key={v} label={v} active={warmth === v}
                  onClick={() => setWarmth((p) => p === v ? null : v)}
                  testid={`filter-warmth-${v}`} />
              ))}
            </FilterGroup>

            {(familyFilter || lightness || warmth || q) && (
              <button
                onClick={() => { setFamilyFilter(null); setLightness(null); setWarmth(null); setQ(""); }}
                data-testid="colours-clear-filters"
                className="mt-4 text-xs text-brand-muted hover:text-brand-ink underline"
              >
                Clear all filters
              </button>
            )}
          </aside>

          <section>
            {!palette ? (
              <div className="p-16 text-center text-brand-muted"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-16 text-center text-brand-muted" data-testid="colours-empty">
                No colours match those filters. Try clearing them.
              </div>
            ) : view === "grid" ? (
              <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {filtered.slice(0, visible).map((c) => (
                  <ColourCard
                    key={c.hex + c.code}
                    colour={c}
                    isFavourite={favourites.includes((c.hex || "").toUpperCase())}
                    onToggleFavourite={() => toggleFav(c.hex)}
                    onPick={() => onPickColour(c)}
                  />
                ))}
              </ul>
            ) : (
              <ul className="space-y-2" data-testid="colours-list">
                {filtered.slice(0, visible).map((c) => (
                  <ColourRow
                    key={c.hex + c.code}
                    colour={c}
                    isFavourite={favourites.includes((c.hex || "").toUpperCase())}
                    onToggleFavourite={() => toggleFav(c.hex)}
                    onPick={() => onPickColour(c)}
                  />
                ))}
              </ul>
            )}

            {visible < filtered.length && (
              <div className="text-center mt-8">
                <button
                  onClick={() => setVisible((v) => Math.min(v + PAGE_SIZE, filtered.length))}
                  data-testid="colours-load-more"
                  className="inline-flex items-center gap-1.5 bg-white border border-brand-border h-10 px-5 rounded-full text-xs font-semibold text-brand-ink hover:border-brand-teal transition-colors"
                >
                  Load more colours
                </button>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div className="mb-6 last:mb-0">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-muted mb-3">{label}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function FilterOption({ label, count, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left px-3 py-1.5 rounded-lg text-xs transition-colors inline-flex items-center justify-between capitalize ${
        active ? "bg-brand-linen text-brand-ink font-semibold" : "text-brand-ink hover:bg-brand-linen/40"
      }`}
    >
      <span>{label}</span>
      {typeof count === "number" && <span className="text-[10px] text-brand-muted">{count}</span>}
    </button>
  );
}

function ColourCard({ colour, isFavourite, onToggleFavourite, onPick }) {
  return (
    <li className="bg-white rounded-2xl overflow-hidden border border-brand-border hover:border-brand-teal hover:shadow-soft transition-all">
      <div className="relative">
        <button
          type="button"
          onClick={onPick}
          data-testid={`colour-card-${colour.code}`}
          className="block w-full h-32 lg:h-36"
          style={{ background: colour.hex }}
          aria-label={`${colour.name} · ${colour.code}`}
        />
        <button
          type="button"
          onClick={onToggleFavourite}
          data-testid={`colour-fav-${colour.code}`}
          aria-label={isFavourite ? "Remove from favourites" : "Add to favourites"}
          className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white/95 backdrop-blur-sm border border-brand-border inline-flex items-center justify-center hover:bg-white shadow-sm transition-all"
        >
          <Heart size={13} className={isFavourite ? "text-red-500 fill-red-500" : "text-brand-muted"} />
        </button>
      </div>
      <div className="px-3 py-2.5">
        <div className="text-sm font-serif text-brand-ink leading-tight truncate">{colour.name}</div>
        <div className="text-[10px] text-brand-muted mt-0.5">{colour.code}</div>
        <div className="text-[10px] uppercase tracking-widest text-brand-muted mt-1.5">{colour.library_family}</div>
      </div>
    </li>
  );
}

function ColourRow({ colour, isFavourite, onToggleFavourite, onPick }) {
  return (
    <li className="bg-white rounded-2xl border border-brand-border hover:border-brand-teal transition-colors flex items-center gap-4 p-3">
      <button
        type="button"
        onClick={onPick}
        data-testid={`colour-row-${colour.code}`}
        className="w-14 h-14 rounded-xl shrink-0"
        style={{ background: colour.hex }}
        aria-label={colour.name}
      />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-serif text-brand-ink truncate">{colour.name}</div>
        <div className="text-[11px] text-brand-muted">{colour.code} · {colour.library_family}</div>
      </div>
      <button
        type="button"
        onClick={onToggleFavourite}
        data-testid={`colour-row-fav-${colour.code}`}
        className="w-9 h-9 rounded-full inline-flex items-center justify-center hover:bg-brand-linen transition-colors"
      >
        <Heart size={14} className={isFavourite ? "text-red-500 fill-red-500" : "text-brand-muted"} />
      </button>
    </li>
  );
}
